import { Search, Play, Clock } from "lucide-react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState, useMemo } from "react";
import { PodcastService, type Podcast } from "@/lib/podcast/podcast.service";
import { getPlayerActions, usePlayerStore } from "@/lib/player/player.store";
import LoadingSpinner from "@/components/loadingSpinner.component";
import { usePodWorkspaces } from "@/features/dashboard/hooks/usePodWorkspaces.hook";
import { PodWorkspaceCard } from "@/features/podWorkspace/components/podWorkspaceCard.component";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * PodWorkspacePage component
 * This is the page that will be used to display all workspaces or podcasts by workspace ID
 * @returns
 */
export default function PodWorkspacePage() {
	const { t } = useTranslation("common");
	const { workspaceId } = useParams<{ workspaceId: string }>();
	const [podcasts, setPodcasts] = useState<Podcast[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");

	// For workspace list view
	const {
		workspaces,
		podcastsByWorkspace,
		isLoading: workspacesLoading,
		error: workspacesError,
		refetch,
	} = usePodWorkspaces({ enabled: !workspaceId });

	// Polling is now handled by usePodWorkspaces hook with conditional refetchInterval
	// No manual setInterval needed anymore

	// Player state
	const currentPodcastId = usePlayerStore((state) => state.podcastId);
	const isPaused = usePlayerStore((state) => state.isPaused);

	useEffect(() => {
		const fetchPodcasts = async () => {
			if (!workspaceId) {
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				const data = await PodcastService.getPodcastsByWorkspace(workspaceId);
				setPodcasts(data);
				setError(null);
			} catch (err) {
				console.error("Failed to fetch podcasts:", err);
				setError(t("workspace.errors.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchPodcasts();
	}, [workspaceId, t]);

	const handlePlayPodcast = async (podcastId: string) => {
		try {
			await getPlayerActions().loadPodcast(podcastId, true);
		} catch (error) {
			console.error("Failed to play podcast:", error);
		}
	};

	const handleOpenChat = (workspaceId: string) => {
		// Navigate to the workspace's chat or open a chat modal
		// You can implement this based on your routing structure
		console.log("Opening chat for workspace:", workspaceId);
		// For now, just log it - you may want to add navigation or modal logic
	};

	const handleRetryPodcast = async (podcastId: string) => {
		try {
			await PodcastService.retryPodcast(podcastId);
			toast.success(t("workspace.list.retrySuccess"));
			await refetch();
		} catch {
			toast.error(t("workspace.errors.retryFailed"));
		}
	};

	// Callback after workspace deletion to refresh data
	const handleWorkspaceDeleted = () => {
		refetch();
	};

	const formatDuration = (milliseconds: number) => {
		const seconds = Math.floor(milliseconds / 1000);
		const minutes = Math.floor(seconds / 60);
		if (minutes < 60) {
			return `${minutes} ${t("workspace.duration.minutes")}`;
		}
		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;
		return `${hours}${t("workspace.duration.hours")} ${remainingMinutes}${t("workspace.duration.minutesShort")}`;
	};

	// Filter workspaces based on search query
	const filteredWorkspaces = useMemo(() => {
		if (!searchQuery.trim()) return workspaces;
		const query = searchQuery.toLowerCase();
		return workspaces.filter((workspace) =>
			workspace.title.toLowerCase().includes(query),
		);
	}, [workspaces, searchQuery]);

	// Show all workspaces view when no workspaceId is specified
	if (!workspaceId) {
		if (workspacesLoading) {
			return (
				<div className="flex flex-col h-full overflow-hidden items-center justify-center">
					<LoadingSpinner />
				</div>
			);
		}

		if (workspacesError) {
			return (
				<div className="flex flex-col h-full overflow-hidden">
					<div className="flex-1 overflow-y-auto">
						<div className="mx-auto max-w-6xl px-8 py-8">
							<div className="mb-8">
								<h1 className="text-3xl font-bold text-foreground">
									{t("common.error")}
								</h1>
								<p className="mt-2 text-destructive">
									{workspacesError.message}
								</p>
							</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="flex flex-col h-full overflow-hidden">
				<div className="flex-1 overflow-y-auto pb-32">
					<div className="mx-auto max-w-6xl px-4 md:px-8 py-8">
						<div className="mb-8">
							<h1 className="text-3xl font-bold text-foreground">
								{t("workspace.list.title")}
							</h1>
							<p className="mt-2 text-muted-foreground">
								{workspaces.length > 0
									? t("workspace.list.workspaceCount", {
											count: workspaces.length,
										})
									: t("workspace.list.noWorkspaces")}
							</p>
						</div>

						{/* Search Bar */}
						{workspaces.length > 0 && (
							<div className="mb-8">
								<div className="relative">
									<Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
									<Input
										type="text"
										placeholder={t("workspace.search.workspacesPlaceholder")}
										className="h-12 pl-12 text-base"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
								</div>
							</div>
						)}

						{/* Workspaces Grid */}
						{filteredWorkspaces.length > 0 ? (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{filteredWorkspaces.map((workspace) => {
									const podcast = podcastsByWorkspace[workspace.id];
									const isCurrentlyPlaying = podcast?.id === currentPodcastId;

									return (
										<PodWorkspaceCard
											key={workspace.id}
											workspace={workspace}
											podcast={podcast}
											isCurrentlyPlaying={isCurrentlyPlaying}
											isPlayerPaused={isPaused}
											onPlayPodcast={handlePlayPodcast}
											onOpenChat={handleOpenChat}
											onRetryPodcast={handleRetryPodcast}
											onWorkspaceDeleted={handleWorkspaceDeleted}
										/>
									);
								})}
							</div>
						) : (
							<div className="text-center py-12">
								<p className="text-muted-foreground">
									{searchQuery
										? t("workspace.list.noWorkspacesFound")
										: t("workspace.list.noWorkspacesYet")}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="flex flex-col h-full overflow-hidden items-center justify-center">
				<LoadingSpinner />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col h-full overflow-hidden">
				<div className="flex-1 overflow-y-auto">
					<div className="mx-auto max-w-6xl px-8 py-8">
						<div className="mb-8">
							<h1 className="text-3xl font-bold text-foreground">
								{t("common.error")}
							</h1>
							<p className="mt-2 text-destructive">{error}</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex-1 overflow-y-auto">
				<div className="mx-auto max-w-6xl px-8 py-8">
					<div className="mb-8">
						<h1 className="text-3xl font-bold text-foreground">
							{t("workspace.detail.title")}
						</h1>
						<p className="mt-2 text-muted-foreground">
							{podcasts.length > 0
								? t("workspace.list.podcastsInWorkspace", {
										count: podcasts.length,
									})
								: t("workspace.list.noPodcastsInWorkspace")}
						</p>
					</div>

					{podcasts.length > 0 && (
						<div className="mb-8">
							<div className="relative">
								<Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
								<Input
									type="text"
									placeholder={t("workspace.search.podcastsPlaceholder")}
									className="h-12 pl-12 text-base"
								/>
							</div>
						</div>
					)}

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
						{podcasts.map((podcast) => {
							const hasProgress =
								podcast.progress && podcast.progress.currentTime > 0;
							const isCompleted = podcast.status === "COMPLETED";
							const notStarted = !hasProgress;

							return (
								<Card
									key={podcast.id}
									className="cursor-pointer border border-border bg-card p-6 transition-all hover:border-primary hover:shadow-lg"
								>
									<div className="flex flex-col gap-3">
										<div className="flex items-start justify-between gap-2">
											<h3 className="font-medium text-foreground line-clamp-2 flex-1">
												{podcast.title}
											</h3>
											{isCompleted && (
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 shrink-0"
													onClick={(e) => {
														e.stopPropagation();
														handlePlayPodcast(podcast.id);
													}}
													title={t("workspace.tooltips.playPodcast")}
												>
													<Play className="h-4 w-4" />
												</Button>
											)}
										</div>

										{/* Status Badge */}
										<div className="flex items-center gap-2">
											<Badge
												variant={
													podcast.status === "COMPLETED"
														? "default"
														: "secondary"
												}
												className="text-xs"
											>
												{podcast.status}
											</Badge>
											{notStarted && podcast.status === "COMPLETED" && (
												<Badge variant="outline" className="text-xs">
													<Clock className="h-3 w-3 mr-1" />
													{t("workspace.list.notStarted")}
												</Badge>
											)}
										</div>

										{/* Progress Bar */}
										{hasProgress && podcast.progress && (
											<div className="space-y-1">
												<div className="w-full bg-secondary rounded-full h-1.5">
													<div
														className="bg-primary h-1.5 rounded-full transition-all"
														style={{
															width: `${Math.min(podcast.progress.progressPercentage, 100)}%`,
														}}
													/>
												</div>
												<p className="text-xs text-muted-foreground">
													{podcast.progress.progressPercentage.toFixed(0)}%{" "}
													{t("workspace.list.complete")}
												</p>
											</div>
										)}

										{/* Podcast metadata */}
										<div className="flex items-center justify-between border-t border-border pt-3">
											<div className="flex items-center gap-2 text-xs text-muted-foreground">
												<span>{formatDuration(podcast.length)}</span>
												<span>•</span>
												<span className="capitalize">{podcast.visibility}</span>
											</div>
											{podcast.progress?.lastListenedAt && (
												<div className="text-xs text-muted-foreground">
													{new Date(
														podcast.progress.lastListenedAt,
													).toLocaleDateString()}
												</div>
											)}
										</div>
									</div>
								</Card>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
